import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  login,
  register,
  sendSmsLogin,
  verifySmsLogin,
  getCurrentUser,
} from "../../../services/companyAdminService";
import { useApp } from "../../../context/AppContext";
import { setCredentials } from "../../../store/authSlice";
import LanguageSelect from "../../../components/LanguageSelect/LanguageSelect";
import { SunIcon, MoonIcon } from "../../../components/icons";
import {
  Container,
  BackgroundDecor,
  LoginCard,
  TopBar,
  IconButton,
  Logo,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  ErrorMessage,
  Divider,
  GoogleButton,
  SignupLink,
  FooterLinks,
} from "./CompanyLogin.style";

/* Varied leaf shapes for organic background */
const LeafRound = ({ className, color }) => (
  <svg
    className={className}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M50 8 C18 35 12 58 28 82 C45 95 65 92 78 78 C92 58 88 32 50 8 Z"
      fill={color}
      opacity={0.92}
    />
  </svg>
);

const LeafPointed = ({ className, color }) => (
  <svg
    className={className}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M50 5 Q20 45 35 85 Q50 98 65 85 Q80 45 50 5 Z"
      fill={color}
      opacity={0.9}
    />
  </svg>
);

const LeafHeart = ({ className, color }) => (
  <svg
    className={className}
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M50 15 C25 15 10 45 50 80 C90 45 75 15 50 15 Z"
      fill={color}
      opacity={0.85}
    />
  </svg>
);

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:3000";

// Views: "login" | "register" | "sms-phone" | "sms-code"
const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const CompanyLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, colors, isRTL, isDark, toggleTheme } = useApp();
  const leafColor = colors.accent;
  const accentOrb = isDark
    ? "rgba(59, 130, 246, 0.07)"
    : "rgba(37, 99, 235, 0.08)";
  const cardBg = hexToRgba(colors.bgSecondary, 0.92);

  const [view, setView] = useState("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Register fields
  const [regCompanyName, setRegCompanyName] = useState("");
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");

  // SMS fields
  const [smsPhone, setSmsPhone] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [smsMasked, setSmsMasked] = useState("");
  const [devOtp, setDevOtp] = useState("");

  const clearFields = () => {
    setError("");
    setDevOtp("");
  };

  // ────── Login ──────
  const handleLogin = async (e) => {
    e.preventDefault();
    clearFields();
    setLoading(true);
    try {
      await login(email, password);
      const token = localStorage.getItem("companyAdminToken");
      const current = getCurrentUser();
      dispatch(setCredentials({ token, user: current?.user, company: current?.company }));
      navigate("/company/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.error?.message || err.message || t("loginFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  // ────── Register ──────
  const handleRegister = async (e) => {
    e.preventDefault();
    clearFields();
    setLoading(true);
    try {
      await register({
        companyName: regCompanyName,
        email: regEmail,
        password: regPassword,
        name: regName,
        phone: regPhone,
      });
      const token = localStorage.getItem("companyAdminToken");
      const current = getCurrentUser();
      dispatch(setCredentials({ token, user: current?.user, company: current?.company }));
      navigate("/company/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.error?.message || err.message || t("loginFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  // ────── SMS Send ──────
  const handleSmsSend = async (e) => {
    e.preventDefault();
    clearFields();
    setLoading(true);
    try {
      const data = await sendSmsLogin(smsPhone);
      setSmsMasked(data.phone || smsPhone);
      if (data._devOtp) setDevOtp(data._devOtp);
      setView("sms-code");
    } catch (err) {
      setError(
        err.response?.data?.error?.message || err.message || t("loginFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  // ────── SMS Verify ──────
  const handleSmsVerify = async (e) => {
    e.preventDefault();
    clearFields();
    setLoading(true);
    try {
      await verifySmsLogin(smsPhone, smsCode);
      const token = localStorage.getItem("companyAdminToken");
      const current = getCurrentUser();
      dispatch(setCredentials({ token, user: current?.user, company: current?.company }));
      navigate("/company/dashboard");
    } catch (err) {
      setError(
        err.response?.data?.error?.message || err.message || t("loginFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const switchView = (v) => {
    setView(v);
    setError("");
    setDevOtp("");
  };

  return (
    <Container
      $colors={colors}
      $isRTL={isRTL}
      $isDark={isDark}
      $accentOrb={accentOrb}
    >
      <BackgroundDecor $isDark={isDark}>
        <LeafRound className="leaf leaf-1 animate" color={leafColor} />
        <LeafPointed className="leaf leaf-2 animate-slow" color={leafColor} />
        <LeafRound className="leaf leaf-3 animate-slow" color={leafColor} />
        <LeafHeart className="leaf leaf-4 animate" color={leafColor} />
        <LeafPointed className="leaf leaf-5 animate" color={leafColor} />
        <LeafRound className="leaf leaf-6 animate-slow" color={leafColor} />
        <LeafHeart className="leaf leaf-7 animate" color={leafColor} />
        <LeafPointed className="leaf leaf-8 animate-slow" color={leafColor} />
        <LeafRound className="leaf blur blur-1" color={leafColor} />
        <LeafPointed className="leaf blur blur-2" color={leafColor} />
      </BackgroundDecor>
      <LoginCard $colors={colors} $isDark={isDark} $cardBg={cardBg}>
        <TopBar>
          <LanguageSelect />
          <IconButton
            $colors={colors}
            onClick={toggleTheme}
            title={isDark ? t("lightMode") : t("darkMode")}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </IconButton>
        </TopBar>

        <Logo $colors={colors}>
          <h1>DevCraft Contracts</h1>
          <p>{t("companyAdminPortal")}</p>
        </Logo>

        {/* ─────────── LOGIN ─────────── */}
        {view === "login" && (
          <>
            <Form onSubmit={handleLogin}>
              {error && <ErrorMessage $colors={colors}>{error}</ErrorMessage>}

              <FormGroup>
                <Label $colors={colors} htmlFor="email">
                  {t("email")}
                </Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </FormGroup>

              <FormGroup>
                <Label $colors={colors} htmlFor="password">
                  {t("password")}
                </Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
              </FormGroup>

              <Button $colors={colors} type="submit" disabled={loading}>
                {loading ? t("signingIn") : t("signIn")}
              </Button>
            </Form>

            <Divider $colors={colors}>
              <span>{t("or")}</span>
            </Divider>

            <GoogleButton
              $colors={colors}
              onClick={() =>
                (window.location.href = `${BACKEND_URL}/api/company/auth/google`)
              }
            >
              <svg viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t("continueWithGoogle")}
            </GoogleButton>

            <div style={{ marginTop: 12 }}>
              <Button
                $colors={colors}
                type="button"
                onClick={() => switchView("sms-phone")}
                style={{
                  background: "transparent",
                  color: colors.accent,
                  border: `1px solid ${colors.border}`,
                  width: "100%",
                }}
              >
                {t("loginWithSms")}
              </Button>
            </div>

            <SignupLink $colors={colors}>
              {t("dontHaveAccount")}{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  switchView("register");
                }}
              >
                {t("createAccount")}
              </a>
            </SignupLink>
          </>
        )}

        {/* ─────────── REGISTER ─────────── */}
        {view === "register" && (
          <>
            <Form onSubmit={handleRegister}>
              {error && <ErrorMessage $colors={colors}>{error}</ErrorMessage>}

              <FormGroup>
                <Label $colors={colors}>{t("companyNameLabel")} *</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  value={regCompanyName}
                  onChange={(e) => setRegCompanyName(e.target.value)}
                  placeholder={isRTL ? "שם החברה שלך" : "Your Company"}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label $colors={colors}>{t("fullName")}</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  value={regName}
                  onChange={(e) => setRegName(e.target.value)}
                  placeholder={isRTL ? "השם שלך" : "Your Name"}
                />
              </FormGroup>

              <FormGroup>
                <Label $colors={colors}>{t("email")} *</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  type="email"
                  value={regEmail}
                  onChange={(e) => setRegEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </FormGroup>

              <FormGroup>
                <Label $colors={colors}>{t("phoneNumber")}</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  type="tel"
                  autoComplete="tel"
                  value={regPhone}
                  onChange={(e) => setRegPhone(e.target.value)}
                  placeholder="0500000000"
                  dir="ltr"
                />
              </FormGroup>

              <FormGroup>
                <Label $colors={colors}>{t("password")} *</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  type="password"
                  value={regPassword}
                  onChange={(e) => setRegPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </FormGroup>

              <Button $colors={colors} type="submit" disabled={loading}>
                {loading ? t("registering") : t("register")}
              </Button>
            </Form>

            <Divider $colors={colors}>
              <span>{t("or")}</span>
            </Divider>

            <GoogleButton
              $colors={colors}
              onClick={() =>
                (window.location.href = `${BACKEND_URL}/api/company/auth/google`)
              }
            >
              <svg viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t("continueWithGoogle")}
            </GoogleButton>

            <SignupLink $colors={colors}>
              {t("alreadyHaveAccount")}{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  switchView("login");
                }}
              >
                {t("backToLogin")}
              </a>
            </SignupLink>
          </>
        )}

        {/* ─────────── SMS - Phone ─────────── */}
        {view === "sms-phone" && (
          <>
            <Form onSubmit={handleSmsSend}>
              {error && <ErrorMessage $colors={colors}>{error}</ErrorMessage>}

              <FormGroup>
                <Label $colors={colors}>{t("phoneNumber")}</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  type="tel"
                  autoComplete="tel"
                  value={smsPhone}
                  onChange={(e) => setSmsPhone(e.target.value)}
                  placeholder="0500000000"
                  required
                  dir="ltr"
                />
              </FormGroup>

              <Button $colors={colors} type="submit" disabled={loading}>
                {loading ? t("sendingCode") : t("sendCode")}
              </Button>
            </Form>

            <SignupLink $colors={colors}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  switchView("login");
                }}
              >
                {t("backToLogin")}
              </a>
            </SignupLink>
          </>
        )}

        {/* ─────────── SMS - Code ─────────── */}
        {view === "sms-code" && (
          <>
            <Form onSubmit={handleSmsVerify}>
              {error && <ErrorMessage $colors={colors}>{error}</ErrorMessage>}

              <p
                style={{
                  color: colors.textMuted,
                  fontSize: 13,
                  textAlign: "center",
                  margin: "0 0 12px",
                }}
              >
                {t("codeSentTo")}
                <strong dir="ltr">{smsMasked}</strong>
              </p>

              {devOtp && (
                <p
                  style={{
                    color: colors.accent,
                    fontSize: 12,
                    textAlign: "center",
                    margin: "0 0 8px",
                    background: colors.bgTertiary,
                    padding: "6px 10px",
                    borderRadius: 6,
                  }}
                >
                  DEV OTP: <strong>{devOtp}</strong>
                </p>
              )}

              <FormGroup>
                <Label $colors={colors}>{t("verificationCode")}</Label>
                <Input
                  $colors={colors}
                  $isRTL={isRTL}
                  value={smsCode}
                  onChange={(e) => setSmsCode(e.target.value)}
                  placeholder="000000"
                  required
                  dir="ltr"
                  maxLength={6}
                  autoComplete="one-time-code"
                  style={{
                    textAlign: "center",
                    fontSize: 20,
                    letterSpacing: 8,
                  }}
                />
              </FormGroup>

              <Button $colors={colors} type="submit" disabled={loading}>
                {loading ? t("verifying") : t("verify")}
              </Button>
            </Form>

            <SignupLink $colors={colors}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setSmsCode("");
                  switchView("sms-phone");
                }}
              >
                {t("resendCode")}
              </a>
              {" | "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  switchView("login");
                }}
              >
                {t("backToLogin")}
              </a>
            </SignupLink>
          </>
        )}

        <FooterLinks $colors={colors}>
          <a href="/docs/api">{t("documentation")}</a>
          <a href="/">{t("home")}</a>
        </FooterLinks>
      </LoginCard>
    </Container>
  );
};

export default CompanyLogin;
